import {parseNetworkEvent,parseNetworkCommand,type HeyDexNetworkEvent,type HeyDexNetworkCommand} from "./heydex-network";
export {HEYDEX_NETWORK_VERSION,NETWORK_TIMEOUT_MS,NETWORK_REQUEST_BYTES,NETWORK_RESPONSE_BYTES,NetworkRequest,NetworkResponse,NetworkAddresses,parseNetworkEvent,parseNetworkCommand} from "./heydex-network";
export type {HeyDexNetworkRequest,HeyDexNetworkResponse,HeyDexNetworkAddress,HeyDexNetworkCall,HeyDexNetworkEvent,HeyDexNetworkCommand} from "./heydex-network";
/**
 * The bridge between the app's HeyDex tab and HeyDex
 * (heyday.news/heydex/), which the tab holds in a WebView.
 *
 * The app names itself in the view's user agent (`appUserAgentName`), so
 * HeyDex knows from its first script, on every load, that it runs inside the
 * app: there the app's tab bar is the way out, and the app's You and Settings
 * own the account. The marker only takes things out of HeyDex's own chrome; it
 * grants nothing.
 *
 * HeyDex posts a `HeyDexEvent` through `window.ReactNativeWebView`: its
 * surface colours whenever its theme resolves, its answer to a system back,
 * a tap on its Settings gear (the app's one Settings holds HeyDex's), and the
 * reader's HeyDex settings once loaded and after every change. The app drives
 * HeyDex with `heydexCommandScript`, which dispatches a
 * `HEYDEX_COMMAND_EVENT` on HeyDex's window. The app's appearance is also left
 * on the window as `HEYDEX_APPEARANCE_GLOBAL`, set before HeyDex's first
 * script runs, so HeyDex's first paint already wears it.
 *
 * In the iOS app HeyDex's box is the app's TrumanKit box: HeyDex posts each
 * box call as a `box` event and the app answers it with a `boxReply` command
 * naming the same id. File contents travel as base64.
 *
 * Neither side trusts the other's message: each is decoded here.
 */

/** The product token the app adds to the WebView's user agent, `HeyDayNewsApp/<version>`. */
export const APP_PRODUCT = "HeyDayNewsApp";

/** The `applicationNameForUserAgent` the app's HeyDex view sets. */
export function appUserAgentName(version: string): string {
  if (!/^[0-9A-Za-z.+-]+$/.test(version)) throw new Error(`not a version: ${version}`);
  return `${APP_PRODUCT}/${version}`;
}

/** Whether a user agent is the app's HeyDex view. */
export function isAppUserAgent(userAgent: string): boolean {
  return new RegExp(`(^|\\s)${APP_PRODUCT}/[0-9A-Za-z.+-]+(\\s|$)`).test(userAgent);
}

export const HEYDEX_COMMAND_EVENT = "heyday-app:command";
export const HEYDEX_APPEARANCE_GLOBAL = "heydayAppAppearance";
export const HEYDEX_THEME_GLOBAL = "heydayAppTheme";

import { type AppThemeId, parseThemeId } from "./themes";

/** The appearance the app resolved from Settings (System follows the device). */
export type Appearance = "light" | "dark";

/** HeyDex's own surface, as colours React Native can read (`#rrggbb`). */
export type HeyDexSurface = {
  scheme: Appearance;
  /** The page behind the conversation and the composer. */
  background: string;
  foreground: string;
  muted: string;
  border: string;
  /** What answers a tap. */
  accent: string;
};

/** A reasoning level; HeyDex maps it to a model and effort the reader never sees. */
export type HeyDexReasoning = "low" | "medium" | "high";

export const HEYDEX_REASONING_LEVELS: readonly HeyDexReasoning[] = ["low", "medium", "high"];

/** The bounds of `sidebarAutoSettleAfterDays`, as HeyDex's `ServerSettings` holds them. */
export const AUTO_SETTLE_DAYS = { min: 1, max: 90 } as const;

/**
 * The reader's HeyDex settings a phone offers. Every key but `defaultReasoning`
 * is named and typed as HeyDex's `ServerSettings` (or client setting) names it.
 */
export type HeyDexSettings = {
  /** The reasoning level a new thread starts with. */
  defaultReasoning: HeyDexReasoning;
  /** `paragraph` shows each finished paragraph as it arrives; `turn` shows the answer when complete. */
  responseStreamingMode: "paragraph" | "turn";
  /** Whole days of inactivity before a thread settles, `AUTO_SETTLE_DAYS` inclusive; null never settles one. */
  sidebarAutoSettleAfterDays: number | null;
  snoozeLimitedThreads: boolean;
  autoResumeLimitedThreads: boolean;
  inAppNotificationsEnabled: boolean;
};

/** The box calls HeyDex makes; the shapes of their arguments are `HeyDexBoxArgs`. */
export type HeyDexBoxMethod = keyof HeyDexBoxArgs;

/** Each box call's arguments. Paths are absolute box paths; `content` is base64. */
export type HeyDexBoxArgs = {
  exec: [command: string, options: { cwd?: string; env?: Record<string, string>; stdin?: string; deadlineMs?: number }];
  interrupt: [];
  readFile: [path: string];
  writeFile: [path: string, content: string];
  stat: [path: string];
  listDir: [path: string];
  mkdir: [path: string, options: { recursive: boolean }];
  remove: [path: string, options: { recursive: boolean; force: boolean }];
  move: [from: string, to: string];
};

export type HeyDexBoxCall = { [M in HeyDexBoxMethod]: { type: "box"; id: number; method: M; args: HeyDexBoxArgs[M] } }[HeyDexBoxMethod];

export type HeyDexEvent =
  | HeyDexNetworkEvent
  | { type: "surface"; surface: HeyDexSurface }
  | HeyDexBoxCall
  /** HeyDex's answer to `back`: `handled` is false when it had nothing to close or go back to. */
  | { type: "back"; handled: boolean }
  /** The reader tapped HeyDex's Settings gear. */
  | { type: "openSettings" }
  /**
   * The reader's settings: after HeyDex loads them, whenever they change, and
   * once each `setSettings` has settled, saved or rejected. That last report
   * names the change it answers in `answers`; no other report carries it.
   */
  | { type: "settings"; settings: HeyDexSettings; answers?: string };

export type HeyDexCommand =
  | HeyDexNetworkCommand
  /** `theme` is Settings' theme for `appearance`, which HeyDex wears in that appearance. */
  | { type: "appearance"; appearance: Appearance; theme: AppThemeId }
  | { type: "back" }
  /** Applied through HeyDex's own settings client, which answers with one `settings` event whose `answers` is `id`. */
  | { type: "setSettings"; id: string; change: Partial<HeyDexSettings> }
  /**
   * The app's answer to the `box` event with the same id. `value` is the
   * method's result as JSON (`readFile` gives base64); a failure carries the
   * box's error code (a POSIX code, `lost` when the box's page ended mid-call,
   * `unavailable` when the box could not start) and message.
   */
  | { type: "boxReply"; id: number; ok: true; value: unknown }
  | { type: "boxReply"; id: number; ok: false; code: string; message: string };

/** The longest id a `setSettings` may carry. */
export const CHANGE_ID_MAX = 64;

/** Decodes a change id: a string of 1 to `CHANGE_ID_MAX` characters. */
export function parseChangeId(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 && value.length <= CHANGE_ID_MAX ? value : null;
}

const HEX = /^#[0-9a-f]{6}$/;

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

/** Decodes an appearance: the one the app left on HeyDex's window, or a field of a message. */
export function parseAppearance(value: unknown): Appearance | null {
  return value === "light" || value === "dark" ? value : null;
}

function hex(value: unknown): string | null {
  return typeof value === "string" && HEX.test(value) ? value : null;
}

function surface(value: unknown): HeyDexSurface | null {
  const fields = record(value);
  const scheme = parseAppearance(fields?.["scheme"]);
  const background = hex(fields?.["background"]);
  const foreground = hex(fields?.["foreground"]);
  const muted = hex(fields?.["muted"]);
  const border = hex(fields?.["border"]);
  const accent = hex(fields?.["accent"]);
  if (!scheme || !background || !foreground || !muted || !border || !accent) return null;
  return { scheme, background, foreground, muted, border, accent };
}

function reasoning(value: unknown): HeyDexReasoning | null {
  return value === "low" || value === "medium" || value === "high" ? value : null;
}

const SETTING_DECODERS: { [K in keyof HeyDexSettings]: (value: unknown) => HeyDexSettings[K] | undefined } = {
  defaultReasoning: (value) => reasoning(value) ?? undefined,
  responseStreamingMode: (value) => (value === "paragraph" || value === "turn" ? value : undefined),
  sidebarAutoSettleAfterDays: (value) =>
    value === null || (Number.isInteger(value) && typeof value === "number" && value >= AUTO_SETTLE_DAYS.min && value <= AUTO_SETTLE_DAYS.max)
      ? value
      : undefined,
  snoozeLimitedThreads: (value) => (typeof value === "boolean" ? value : undefined),
  autoResumeLimitedThreads: (value) => (typeof value === "boolean" ? value : undefined),
  inAppNotificationsEnabled: (value) => (typeof value === "boolean" ? value : undefined),
};

const SETTING_KEYS = Object.keys(SETTING_DECODERS) as (keyof HeyDexSettings)[];

function decodeSetting<K extends keyof HeyDexSettings>(fields: Record<string, unknown>, key: K, into: Partial<HeyDexSettings>): boolean {
  if (!Object.hasOwn(fields, key)) return true;
  const decoded = SETTING_DECODERS[key](fields[key]);
  if (decoded === undefined) return false;
  into[key] = decoded;
  return true;
}

/** Decodes a settings change: at least one field, every present field valid; other keys are dropped. */
export function parseHeyDexSettingsChange(value: unknown): Partial<HeyDexSettings> | null {
  const fields = record(value);
  if (!fields) return null;
  const change: Partial<HeyDexSettings> = {};
  for (const key of SETTING_KEYS) if (!decodeSetting(fields, key, change)) return null;
  return Object.keys(change).length > 0 ? change : null;
}

/** Decodes a whole `HeyDexSettings`; anything incomplete or malformed is null. */
export function parseHeyDexSettings(value: unknown): HeyDexSettings | null {
  const c = parseHeyDexSettingsChange(value);
  if (
    c?.defaultReasoning === undefined ||
    c.responseStreamingMode === undefined ||
    c.sidebarAutoSettleAfterDays === undefined ||
    c.snoozeLimitedThreads === undefined ||
    c.autoResumeLimitedThreads === undefined ||
    c.inAppNotificationsEnabled === undefined
  )
    return null;
  return {
    defaultReasoning: c.defaultReasoning,
    responseStreamingMode: c.responseStreamingMode,
    sidebarAutoSettleAfterDays: c.sidebarAutoSettleAfterDays,
    snoozeLimitedThreads: c.snoozeLimitedThreads,
    autoResumeLimitedThreads: c.autoResumeLimitedThreads,
    inAppNotificationsEnabled: c.inAppNotificationsEnabled,
  };
}

function strings(value: unknown): Record<string, string> | null {
  const fields = record(value);
  if (!fields) return null;
  return Object.values(fields).every((entry) => typeof entry === "string") ? (fields as Record<string, string>) : null;
}

const optional = <T>(fields: Record<string, unknown>, key: string, decode: (value: unknown) => T | null): T | null | undefined =>
  Object.hasOwn(fields, key) ? decode(fields[key]) : undefined;

function execOptions(value: unknown): HeyDexBoxArgs["exec"][1] | null {
  const fields = record(value);
  if (!fields) return null;
  const options: HeyDexBoxArgs["exec"][1] = {};
  const cwd = optional(fields, "cwd", (v) => (typeof v === "string" ? v : null));
  const env = optional(fields, "env", strings);
  const stdin = optional(fields, "stdin", (v) => (typeof v === "string" ? v : null));
  const deadlineMs = optional(fields, "deadlineMs", (v) => (typeof v === "number" && Number.isInteger(v) && v > 0 ? v : null));
  if (cwd === null || env === null || stdin === null || deadlineMs === null) return null;
  if (cwd !== undefined) options.cwd = cwd;
  if (env !== undefined) options.env = env;
  if (stdin !== undefined) options.stdin = stdin;
  if (deadlineMs !== undefined) options.deadlineMs = deadlineMs;
  return options;
}

const flags = <K extends string>(value: unknown, keys: readonly K[]): Record<K, boolean> | null => {
  const fields = record(value);
  return fields && keys.every((key) => typeof fields[key] === "boolean") ? (Object.fromEntries(keys.map((key) => [key, fields[key]])) as Record<K, boolean>) : null;
};

const isString = (value: unknown): value is string => typeof value === "string";

function boxCall(id: number, method: unknown, args: unknown[]): HeyDexBoxCall | null {
  const [a, b] = args;
  switch (method) {
    case "exec": {
      const options = execOptions(b);
      return isString(a) && options && args.length === 2 ? { type: "box", id, method, args: [a, options] } : null;
    }
    case "interrupt":
      return args.length === 0 ? { type: "box", id, method, args: [] } : null;
    case "readFile":
    case "stat":
    case "listDir":
      return isString(a) && args.length === 1 ? { type: "box", id, method, args: [a] } : null;
    case "writeFile":
    case "move":
      return isString(a) && isString(b) && args.length === 2 ? { type: "box", id, method, args: [a, b] } : null;
    case "mkdir": {
      const options = flags(b, ["recursive"]);
      return isString(a) && options && args.length === 2 ? { type: "box", id, method, args: [a, options] } : null;
    }
    case "remove": {
      const options = flags(b, ["recursive", "force"]);
      return isString(a) && options && args.length === 2 ? { type: "box", id, method, args: [a, options] } : null;
    }
    default:
      return null;
  }
}

/** Decodes a message HeyDex posted; anything else is null. */
export function parseHeyDexEvent(data: string): HeyDexEvent | null {
  let raw: unknown;
  try {
    raw = JSON.parse(data);
  } catch {
    return null;
  }
  const fields = record(raw);
  if(fields?.["type"] === "network" || fields?.["type"] === "networkReady" || fields?.["type"] === "networkClose")return parseNetworkEvent(fields);
  if (fields?.["type"] === "surface") {
    const value = surface(fields["surface"]);
    return value ? { type: "surface", surface: value } : null;
  }
  if (fields?.["type"] === "back" && typeof fields["handled"] === "boolean") return { type: "back", handled: fields["handled"] };
  if (fields?.["type"] === "openSettings") return { type: "openSettings" };
  if (fields?.["type"] === "box") {
    const id = fields["id"];
    const args = fields["args"];
    return typeof id === "number" && Number.isSafeInteger(id) && Array.isArray(args) ? boxCall(id, fields["method"], args) : null;
  }
  if (fields?.["type"] === "settings") {
    const settings = parseHeyDexSettings(fields["settings"]);
    if (!settings) return null;
    if (!Object.hasOwn(fields, "answers")) return { type: "settings", settings };
    const answers = parseChangeId(fields["answers"]);
    return answers ? { type: "settings", settings, answers } : null;
  }
  return null;
}

/** Decodes a command the app dispatched; anything else is null. */
export function parseHeyDexCommand(value: unknown): HeyDexCommand | null {
  const fields = record(value);
  if(fields?.["type"] === "networkReply" || fields?.["type"] === "networkReady")return parseNetworkCommand(fields);
  if (fields?.["type"] === "back") return { type: "back" };
  if (fields?.["type"] === "boxReply") {
    const id = fields["id"];
    if (typeof id !== "number" || !Number.isSafeInteger(id)) return null;
    if (fields["ok"] === true && Object.hasOwn(fields, "value")) return { type: "boxReply", id, ok: true, value: fields["value"] };
    const code = fields["code"];
    const message = fields["message"];
    return fields["ok"] === false && isString(code) && isString(message) ? { type: "boxReply", id, ok: false, code, message } : null;
  }
  if (fields?.["type"] === "setSettings") {
    const id = parseChangeId(fields["id"]);
    const change = parseHeyDexSettingsChange(fields["change"]);
    return id && change ? { type: "setSettings", id, change } : null;
  }
  const chosen = parseAppearance(fields?.["appearance"]);
  const theme = parseThemeId(fields?.["theme"]);
  return fields?.["type"] === "appearance" && chosen && theme ? { type: "appearance", appearance: chosen, theme } : null;
}

/**
 * JavaScript the app injects into HeyDex to run one command. An appearance and
 * its theme are also left on the window, so the script works before HeyDex has loaded.
 */
export function heydexCommandScript(command: HeyDexCommand): string {
  const keep =
    command.type === "appearance"
      ? `window[${JSON.stringify(HEYDEX_APPEARANCE_GLOBAL)}]=${JSON.stringify(command.appearance)};window[${JSON.stringify(HEYDEX_THEME_GLOBAL)}]=${JSON.stringify(command.theme)};`
      : "";
  return `${keep}window.dispatchEvent(new CustomEvent(${JSON.stringify(HEYDEX_COMMAND_EVENT)},{detail:${JSON.stringify(command)}}));true;`;
}
